import {
  closeSnackbar,
  CustomContentProps,
  enqueueSnackbar,
  OptionsObject,
  SnackbarContent,
} from 'notistack';
import { forwardRef, ReactNode } from 'react';
import {
  MdCheck,
  MdClose,
  MdErrorOutline,
  MdOutlineSchedule,
} from 'react-icons/md';

/// Issue d'un ordre, telle qu'on l'annonce : envoyé, confié au moteur de
/// risque, refusé. Sa réception par le compte a sa propre carte
/// (`notifyOrderProgress`).
export type OrderTone = 'sent' | 'watched' | 'rejected';

declare module 'notistack' {
  interface VariantOverrides {
    order: { tone: OrderTone; detail?: string };
  }
}

const TONES: Record<OrderTone, { color: string; icon: ReactNode }> = {
  sent: { color: '#818cf8', icon: <MdCheck size={16} /> },
  watched: { color: '#818cf8', icon: <MdOutlineSchedule size={16} /> },
  rejected: { color: '#f87171', icon: <MdErrorOutline size={16} /> },
};

interface OrderSnackbarProps extends CustomContentProps {
  tone: OrderTone;
  detail?: string;
}

/// Carte d'un ordre : pastille teintée selon l'issue, titre court, détail en
/// dessous. Grise et sobre, comme les autres cartes de l'application.
const OrderSnackbar = forwardRef<HTMLDivElement, OrderSnackbarProps>(
  ({ id, message, tone, detail, style, className }, ref) => {
    const { color, icon } = TONES[tone];
    return (
      <SnackbarContent ref={ref} role="alert" style={style} className={className}>
        <div className="flex w-[340px] max-w-[calc(100vw-32px)] items-start gap-3 rounded-[9px] border border-white/[0.08] bg-[#141417] py-2.5 pl-3 pr-2 font-main shadow-[0_12px_32px_rgba(0,0,0,0.5)]">
          <span
            className="mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{ color, backgroundColor: `${color}24` }}
          >
            {icon}
          </span>
          <div className="min-w-0 flex-1 py-0.5">
            <div className="text-[13px] font-semibold leading-5 text-neutral-100">
              {message}
            </div>
            {detail && (
              <div className="mt-0.5 text-[12px] leading-4 text-neutral-400">
                {detail}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => closeSnackbar(id)}
            className="rounded p-1 text-neutral-500 transition-colors hover:bg-white/5 hover:text-neutral-200"
          >
            <MdClose size={14} />
          </button>
        </div>
      </SnackbarContent>
    );
  },
);
OrderSnackbar.displayName = 'OrderSnackbar';

export default OrderSnackbar;

/// Annonce l'issue d'un ordre, depuis le graphique comme depuis le terminal.
export function notifyOrder(
  tone: OrderTone,
  title: string,
  detail?: string,
  options?: Omit<OptionsObject<'order'>, 'variant' | 'tone' | 'detail'>,
): void {
  enqueueSnackbar(title, {
    autoHideDuration: tone === 'rejected' ? 8000 : 5000,
    // Deux ordres de suite portent le même titre : chacun garde sa carte.
    preventDuplicate: false,
    ...options,
    variant: 'order',
    tone,
    detail,
  });
}
